import { EventEmitter } from '@angular/core';
import {
  AbstractControl,
  FormControlStatus,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';
import { distinctUntilChangedDeep } from '@common';
import { filter, merge, Observable, of, skip, startWith } from 'rxjs';
import { FormControlValidator } from '../../models/form-control-validator.enum';

export function applyToDescendants(
  control: AbstractControl | null | undefined,
  apply: (...controls: AbstractControl[]) => unknown,
): void {
  if (control instanceof UntypedFormGroup) {
    apply(...Object.values(control.controls));
  }
  if (control instanceof UntypedFormArray) {
    apply(...control.controls);
  }
}

export function getAllFormControls(
  control?: AbstractControl | null,
): AbstractControl[] {
  if (control instanceof UntypedFormGroup) {
    return Object.values(control.controls).reduce(
      (
        descendantControls: AbstractControl[],
        descendantControl: AbstractControl,
      ) => {
        return descendantControls.concat(getAllFormControls(descendantControl));
      },
      [control],
    );
  }
  if (control instanceof UntypedFormArray) {
    return control.controls.reduce(
      (
        descendantControls: AbstractControl[],
        descendantControl: AbstractControl,
      ) => {
        return descendantControls.concat(getAllFormControls(descendantControl));
      },
      [control],
    );
  }
  if (control instanceof UntypedFormControl) {
    return [control];
  }
  return [];
}

export function getAllDisabledFormControls(
  control?: AbstractControl | null,
): AbstractControl[] {
  const controls = getAllFormControls(control);
  const disabledControls = controls.filter(
    (descendantControl: AbstractControl) => descendantControl.disabled,
  );
  return disabledControls;
}

export function enableFormControls(
  ...controls: (AbstractControl | null | undefined)[]
): void {
  controls.forEach((control) => {
    control?.enable();
    applyToDescendants(control, enableFormControls);
  });
}

export function disableFormControls(
  ...controls: (AbstractControl | null | undefined)[]
): void {
  controls.forEach((control) => {
    control?.disable();
    applyToDescendants(control, disableFormControls);
  });
}

export function controlStatus$(
  ...controls: (AbstractControl | undefined | null)[]
): Observable<unknown> {
  return merge(
    ...controls.map(
      (control?: AbstractControl | null) =>
        control?.statusChanges.pipe(startWith(control.status)) ?? of(undefined),
    ),
  );
}

export function controlStatusChanges$(
  ...controls: (AbstractControl | undefined | null)[]
): Observable<unknown> {
  return merge(
    ...controls.map((control?: AbstractControl | null) =>
      controlStatus$(control).pipe(skip(1)),
    ),
  );
}

export function markControlAsTouched(
  ...controls: (AbstractControl | undefined | null)[]
): void {
  controls.forEach((control?: AbstractControl | null) => {
    if (!control) {
      return;
    }
    control.markAsTouched();
    (control.statusChanges as EventEmitter<FormControlStatus>).emit(
      control.status,
    );
    applyToDescendants(control, markControlAsTouched);
  });
}

export function controlValue$<T>(
  ...controls: (AbstractControl | undefined | null)[]
): Observable<T | undefined> {
  return merge(
    ...controls.map(
      (control?: AbstractControl | null) =>
        control?.valueChanges.pipe(
          startWith(control.value),
          filter(() => control.enabled),
          distinctUntilChangedDeep(),
        ) ?? of(undefined),
    ),
  );
}

export function controlValueChanges$<T>(
  ...controls: (AbstractControl | undefined | null)[]
): Observable<T | undefined> {
  return merge(
    ...controls.map((control?: AbstractControl | null) =>
      controlValue$<T>(control).pipe(skip(1)),
    ),
  );
}

export function controlIsRequired(control?: AbstractControl | null): boolean {
  if (!control) {
    return false;
  }
  const validationErrors: ValidationErrors | null = control.validator
    ? control.validator(new UntypedFormControl())
    : {};

  const validationKeys: string[] = Object.keys(validationErrors || {});

  return validationKeys.includes(FormControlValidator.Required);
}
